import { motion } from "framer-motion";

// Points are approximate lon/lat mapped to viewBox 1000x500 (Equirectangular)
// lon: -180..180 -> x: 0..1000 ; lat: 90..-90 -> y: 0..500
export function lonLatToXY(lon, lat) {
  const x = ((lon + 180) / 360) * 1000;
  const y = ((90 - lat) / 180) * 500;
  return { x, y };
}

export const HUBS = [
  { name: "New York", lon: -74, lat: 40.7 },
  { name: "London", lon: -0.1, lat: 51.5 },
  { name: "Zurich", lon: 8.5, lat: 47.4 },
  { name: "Dubai", lon: 55.3, lat: 25.2 },
  { name: "Singapore", lon: 103.8, lat: 1.35 },
  { name: "Hong Kong", lon: 114.2, lat: 22.3 },
  { name: "Tokyo", lon: 139.7, lat: 35.7 },
  { name: "Sydney", lon: 151.2, lat: -33.9 },
  { name: "São Paulo", lon: -46.6, lat: -23.5 },
  { name: "Johannesburg", lon: 28.0, lat: -26.2 },
  { name: "Mumbai", lon: 72.9, lat: 19.1 },
  { name: "Frankfurt", lon: 8.7, lat: 50.1 },
  { name: "Toronto", lon: -79.4, lat: 43.7 },
  { name: "Mexico City", lon: -99.1, lat: 19.4 },
];

// Some routes between hubs
export const ROUTES = [
  ["New York", "London"],
  ["London", "Zurich"],
  ["London", "Dubai"],
  ["Zurich", "Frankfurt"],
  ["Dubai", "Mumbai"],
  ["Mumbai", "Singapore"],
  ["Singapore", "Hong Kong"],
  ["Hong Kong", "Tokyo"],
  ["Tokyo", "Sydney"],
  ["New York", "São Paulo"],
  ["São Paulo", "Johannesburg"],
  ["Johannesburg", "Dubai"],
  ["Toronto", "London"],
  ["New York", "Mexico City"],
  ["Frankfurt", "Dubai"],
];

export default function WorldMap({ compact = false, activeHubs = [], showRoutes = true }) {
  const hubMap = Object.fromEntries(HUBS.map(h => [h.name, lonLatToXY(h.lon, h.lat)]));

  return (
    <div className="relative w-full">
      <svg
        viewBox="0 0 1000 500"
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        data-testid="world-map"
      >
        {/* dotted world backdrop */}
        <defs>
          <pattern id="dots" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.7" fill="#9EABC0" opacity="0.18" />
          </pattern>
          <radialGradient id="glowGold" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Land silhouettes drawn as simplified paths using pattern fill (dotted-earth look) */}
        <g fill="url(#dots)" stroke="rgba(158,171,192,0.15)" strokeWidth="0.5">
          {/* North America */}
          <path d="M120,110 Q160,80 220,90 T310,110 Q340,140 320,180 L280,220 Q240,240 200,230 L160,220 Q130,200 120,170 Z" />
          {/* Central & South America */}
          <path d="M260,240 Q290,260 300,300 Q310,360 280,410 Q260,430 240,410 Q220,380 230,340 Q240,290 260,240 Z" />
          {/* Europe */}
          <path d="M470,110 Q510,95 550,110 Q580,130 570,160 Q540,175 500,170 Q470,160 470,110 Z" />
          {/* Africa */}
          <path d="M490,190 Q540,180 570,210 Q590,260 580,320 Q560,380 530,400 Q500,380 490,340 Q470,290 490,190 Z" />
          {/* Asia */}
          <path d="M580,110 Q660,90 760,110 Q830,140 850,190 Q830,230 780,240 Q700,230 640,210 Q590,180 580,110 Z" />
          {/* SE Asia / India */}
          <path d="M660,220 Q700,230 730,260 Q740,290 710,310 Q680,300 660,270 Z" />
          {/* Australia */}
          <path d="M800,340 Q860,330 890,360 Q890,395 850,410 Q810,405 790,380 Z" />
        </g>

        {/* Routes */}
        {showRoutes && ROUTES.map(([a, b], i) => {
          const A = hubMap[a]; const B = hubMap[b];
          if (!A || !B) return null;
          // Great-circle-ish curve via quadratic control
          const mx = (A.x + B.x) / 2;
          const my = (A.y + B.y) / 2 - Math.abs(A.x - B.x) * 0.12;
          return (
            <path
              key={i}
              d={`M${A.x},${A.y} Q${mx},${my} ${B.x},${B.y}`}
              fill="none"
              stroke="#D4AF37"
              strokeOpacity="0.55"
              strokeWidth="1"
              className="aegis-route"
            />
          );
        })}

        {/* Hubs */}
        {HUBS.map((h) => {
          const p = hubMap[h.name];
          const active = activeHubs.includes(h.name);
          return (
            <g key={h.name}>
              {active && (
                <circle cx={p.x} cy={p.y} r="14" fill="url(#glowGold)" />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r="3.2"
                fill="#D4AF37"
                stroke="#F0F4F8"
                strokeWidth="0.5"
              />
              <circle
                cx={p.x}
                cy={p.y}
                r="3.2"
                fill="#D4AF37"
                className="aegis-pulse"
                opacity="0.7"
              />
              {!compact && (
                <text
                  x={p.x + 6}
                  y={p.y - 5}
                  fill="#9EABC0"
                  fontSize="9"
                  fontFamily="JetBrains Mono, monospace"
                  style={{ letterSpacing: "0.1em" }}
                >
                  {h.name.toUpperCase()}
                </text>
              )}
            </g>
          );
        })}

        {/* Grid lines for aesthetic */}
        {[100,200,300,400].map(y => (
          <line key={y} x1="0" x2="1000" y1={y} y2={y} stroke="#9EABC0" strokeOpacity="0.05" />
        ))}
        {[200,400,600,800].map(x => (
          <line key={x} x1={x} x2={x} y1="0" y2="500" stroke="#9EABC0" strokeOpacity="0.05" />
        ))}
      </svg>

      {/* corner brackets */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[#D4AF37]/70" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[#D4AF37]/70" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-[#D4AF37]/70" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[#D4AF37]/70" />
      </div>
    </div>
  );
}
